import { ref, type Ref } from 'vue'
import { PushNotifications, type PermissionStatus } from '@capacitor/push-notifications'
import { api } from '@/services/api'
import { useToastStore } from '@/stores/toast'

const TOKEN_STORAGE_KEY = 'dhikr_fcm_token'

const isSupported = ref(true)
const permissionState: Ref<PermissionStatus['receive']> = ref('prompt')
const fcmToken = ref<string | null>(localStorage.getItem(TOKEN_STORAGE_KEY))

export function useNativePushNotifications() {
  const toast = useToastStore()
  let listenersRegistered = false

  async function initialize(): Promise<boolean> {
    if (listenersRegistered) return true

    await PushNotifications.addListener('registration', async (token) => {
      fcmToken.value = token.value
      localStorage.setItem(TOKEN_STORAGE_KEY, token.value)
      await registerDeviceToken(token.value)
    })

    await PushNotifications.addListener('registrationError', (error) => {
      console.error('Push registration error:', error)
    })

    await PushNotifications.addListener('pushNotificationReceived', (notification) => {
      const title = notification.title || 'Dhikr Session'
      const body = notification.body || ''
      toast.show(body ? `${title}: ${body}` : title, 'info')
    })

    listenersRegistered = true
    return true
  }

  async function requestPermissionAndGetToken(): Promise<string | null> {
    await initialize()

    let permission = await PushNotifications.checkPermissions()
    if (permission.receive === 'prompt' || permission.receive === 'prompt-with-rationale') {
      permission = await PushNotifications.requestPermissions()
    }
    permissionState.value = permission.receive

    if (permission.receive !== 'granted') {
      return null
    }

    await PushNotifications.register()
    return fcmToken.value
  }

  async function registerDeviceToken(token?: string): Promise<void> {
    const activeToken = token || fcmToken.value
    if (!activeToken) return

    const lastSent = localStorage.getItem('dhikr_fcm_token_sent')
    if (lastSent === activeToken) return

    try {
      await api.updateDeviceToken(activeToken)
      localStorage.setItem('dhikr_fcm_token_sent', activeToken)
    } catch (error) {
      console.error('Failed to register device token with server:', error)
    }
  }

  function clearToken() {
    fcmToken.value = null
    localStorage.removeItem(TOKEN_STORAGE_KEY)
    localStorage.removeItem('dhikr_fcm_token_sent')
  }

  return {
    isSupported,
    permissionState,
    fcmToken,
    initialize,
    requestPermissionAndGetToken,
    registerDeviceToken,
    clearToken,
  }
}
